/**
 * SculptureModel Component
 * Lädt GLB oder PLY (Gaussian Splat) Modelle
 * Zentriert automatisch basierend auf BoundingBox
 * Touch/Drag Rotation um Y-Achse
 */

import { Suspense, useEffect, useMemo, useRef, useState } from 'react'
import { useGLTF } from '@react-three/drei'
import { useFrame, useThree } from '@react-three/fiber'
import { Box3, Vector3, Group, Mesh, Color, Box3Helper, SphereGeometry, BoxGeometry, MeshBasicMaterial } from 'three'

// Debug flag - set to true to show bounding boxes
const SHOW_BOUNDING_BOX = false
import * as GaussianSplats3D from '@mkkellogg/gaussian-splats-3d'
import { Artwork } from '@/types/artwork'
import { useTheme } from '@/hooks/useTheme'
import { mediaUrl } from '@/utils/mediaUrl'

// Bounding box info for camera positioning
export interface BBoxInfo {
  min: { x: number; y: number; z: number }
  max: { x: number; y: number; z: number }
  center: { x: number; y: number; z: number }
}

interface SculptureModelProps {
  artwork: Artwork
  onLoad?: () => void
  onError?: (error: Error) => void
  onBBoxCalculated?: (bbox: BBoxInfo) => void
}

function GLBModel({ url, onBBoxCalculated }: { url: string; onBBoxCalculated?: (bbox: BBoxInfo) => void }) {
  const { scene: originalScene } = useGLTF(url)
  const groupRef = useRef<Group>(null)
  const { gl } = useThree()

  // Rotation state lives in refs, not useState. Earlier versions called
  // setRotationY() from inside useFrame which fires every render frame
  // (60×/s) for every mounted sculpture (6 on the HomePage). That
  // resulted in ~360 React reconciliation cycles per second — a slow
  // memory leak that crashed Chrome's renderer with Code 5 after a few
  // minutes of idle. Refs mutate without triggering a re-render, and
  // the rotation is applied directly to the group's matrix.
  const isDraggingRef = useRef(false)
  const isHoveredRef = useRef(false)
  const rotationYRef = useRef(0)
  // Pitch (vertical tilt) — Hermine #40. Asymmetric clamp: the visitor
  // can tilt UP (looking down at the sculpture) but is blocked from
  // tilting BELOW neutral — Anna's photoscan captures don't have any
  // mesh on the underside of the base, so any downward camera angle
  // exposes the hollow back-side of the disc and looks broken.
  const rotationXRef = useRef(0)
  const PITCH_MAX = Math.PI / 3 // 60° upward
  const PITCH_MIN = 0           // never below neutral
  const lastX = useRef(0)
  const lastY = useRef(0)
  // Auto-rotation continues whenever the user isn't actively dragging.
  // 0.15 rad/s ≈ one full revolution every ~42 s — slow museum spin.
  // Hermine review #44: respect `prefers-reduced-motion` and pause on
  // hover so visitors can study a sculpture without having to drag.
  const AUTO_ROTATE_RATE = 0.15
  const prefersReducedMotion = typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches

  // Clone scene to avoid modifying cached version.
  //
  // Normals: only compute them when the GLB ships WITHOUT a NORMAL
  // attribute. Recomputing unconditionally (as this did until
  // 2026-08-12) destroys good normals on Blender-processed meshes:
  // the 3D-API's Blender pass splits vertices per triangle (Phönixin:
  // 34 526 triangles / 102 153 vertices ≈ 3 per face), so no vertex is
  // shared between faces. computeVertexNormals() then assigns each
  // vertex its own face normal — i.e. flat shading — and every single
  // triangle catches the light differently. On a photoscan texture
  // that reads as a gold/black speckle across the whole sculpture
  // (Alex's report, verified by A/B render: with the call the surface
  // is shattered, without it the bronze is smooth).
  //
  // The original reason for the call still holds for the other path:
  // raw Polycam exports carry no normals at all, and without any the
  // model renders unlit (solid black). So: compute only if missing.
  //
  // flatShading=false stays unconditional — it costs nothing and
  // guarantees the smooth normals are actually used.
  const clonedScene = useMemo(() => {
    const cloned = originalScene.clone(true)
    cloned.traverse((child) => {
      const mesh = child as Mesh
      if (!mesh.isMesh) return
      if (mesh.geometry && !mesh.geometry.attributes.normal) {
        // computeVertexNormals() always writes the attribute, so no
        // second existence check is needed here.
        mesh.geometry.computeVertexNormals()
      }
      const mat = mesh.material as any
      if (mat) {
        if (Array.isArray(mat)) {
          mat.forEach((m) => { if (m && 'flatShading' in m) m.flatShading = false })
        } else if ('flatShading' in mat) {
          mat.flatShading = false
        }
      }
    })
    return cloned
  }, [originalScene, url])

  // Reset rotation when URL changes (new sculpture)
  useEffect(() => {
    rotationYRef.current = 0
  }, [url])

  // Calculate bbox, scale and center
  const { scale, groupOffset, debugBox } = useMemo(() => {
    // Reset cloned scene to get accurate bbox
    clonedScene.position.set(0, 0, 0)
    clonedScene.rotation.set(0, 0, 0)
    clonedScene.scale.set(1, 1, 1)
    clonedScene.updateMatrixWorld(true)

    const box = new Box3().setFromObject(clonedScene)
    const size = box.getSize(new Vector3())
    const center = box.getCenter(new Vector3())

    // Scale to fill screen height (~2.85 units = 95% of 3)
    const targetHeight = 2.85
    const maxDim = Math.max(size.x, size.y, size.z)
    const s = maxDim > 0 ? targetHeight / maxDim : 1

    // Group offset = -center * scale (in world space), shifted down by 0.15
    const groupOffset = new Vector3(
      -center.x * s,
      -center.y * s - 0.15,
      -center.z * s
    )

    // Create scaled bounding box for debug visualization
    const scaledBox = new Box3(
      box.min.clone().multiplyScalar(s).add(groupOffset),
      box.max.clone().multiplyScalar(s).add(groupOffset)
    )

    console.log(`GLB ${url}: size=${size.toArray()}, scale=${s}, center=${center.toArray()}`)

    return { scale: s, groupOffset, debugBox: scaledBox }
  }, [clonedScene, url])

  // Report bbox to parent for camera positioning
  useEffect(() => {
    if (onBBoxCalculated && debugBox) {
      const bboxCenter = new Vector3()
      debugBox.getCenter(bboxCenter)
      onBBoxCalculated({
        min: { x: debugBox.min.x, y: debugBox.min.y, z: debugBox.min.z },
        max: { x: debugBox.max.x, y: debugBox.max.y, z: debugBox.max.z },
        center: { x: bboxCenter.x, y: bboxCenter.y, z: bboxCenter.z },
      })
    }
  }, [debugBox, onBBoxCalculated])

  // Touch/Mouse handlers
  useEffect(() => {
    const canvas = gl.domElement

    const handleStart = (clientX: number, clientY: number) => {
      isDraggingRef.current = true
      lastX.current = clientX
      lastY.current = clientY
    }

    const handleMove = (clientX: number, clientY: number) => {
      if (!isDraggingRef.current) return
      const deltaX = clientX - lastX.current
      const deltaY = clientY - lastY.current
      // Horizontal drag → yaw (#40 — existing behaviour).
      rotationYRef.current += deltaX * 0.01
      // Vertical drag → pitch, clamped (#40). Lower bound 0 so we
      // never reveal the photoscan's unmodeled base underside.
      rotationXRef.current = Math.max(
        PITCH_MIN,
        Math.min(PITCH_MAX, rotationXRef.current + deltaY * 0.01),
      )
      lastX.current = clientX
      lastY.current = clientY
    }

    const handleEnd = () => {
      isDraggingRef.current = false
    }

    // Mouse events
    const onMouseDown = (e: MouseEvent) => handleStart(e.clientX, e.clientY)
    const onMouseMove = (e: MouseEvent) => handleMove(e.clientX, e.clientY)
    const onMouseUp = () => handleEnd()

    // Touch events
    const onTouchStart = (e: TouchEvent) => handleStart(e.touches[0].clientX, e.touches[0].clientY)
    const onTouchMove = (e: TouchEvent) => handleMove(e.touches[0].clientX, e.touches[0].clientY)
    const onTouchEnd = () => handleEnd()

    // Hover detection for auto-rotate-pause (#44).
    const onMouseEnter = () => { isHoveredRef.current = true }
    const onMouseLeaveCanvas = () => { isHoveredRef.current = false }

    canvas.addEventListener('mousedown', onMouseDown)
    canvas.addEventListener('mousemove', onMouseMove)
    canvas.addEventListener('mouseup', onMouseUp)
    canvas.addEventListener('mouseleave', onMouseUp)
    canvas.addEventListener('mouseenter', onMouseEnter)
    canvas.addEventListener('mouseleave', onMouseLeaveCanvas)
    canvas.addEventListener('touchstart', onTouchStart)
    canvas.addEventListener('touchmove', onTouchMove)
    canvas.addEventListener('touchend', onTouchEnd)

    return () => {
      canvas.removeEventListener('mousedown', onMouseDown)
      canvas.removeEventListener('mousemove', onMouseMove)
      canvas.removeEventListener('mouseup', onMouseUp)
      canvas.removeEventListener('mouseleave', onMouseUp)
      canvas.removeEventListener('mouseenter', onMouseEnter)
      canvas.removeEventListener('mouseleave', onMouseLeaveCanvas)
      canvas.removeEventListener('touchstart', onTouchStart)
      canvas.removeEventListener('touchmove', onTouchMove)
      canvas.removeEventListener('touchend', onTouchEnd)
    }
  }, [gl])

  // Apply rotation. Auto-spin advances rotationYRef while the user isn't
  // actively dragging AND isn't hovering (#44 — visitors study the work)
  // AND hasn't set `prefers-reduced-motion` (a11y). Mutating refs + direct
  // group mutation means useFrame triggers ZERO React renders per second.
  useFrame((_, delta) => {
    const shouldAutoRotate =
      !isDraggingRef.current &&
      !isHoveredRef.current &&
      !prefersReducedMotion
    if (shouldAutoRotate) {
      rotationYRef.current += AUTO_ROTATE_RATE * delta
      // Slowly ease the pitch back to neutral when the visitor isn't
      // actively tilting — so the sculpture returns to its hero pose.
      if (Math.abs(rotationXRef.current) > 0.001) {
        rotationXRef.current *= Math.max(0, 1 - delta * 0.5)
      } else {
        rotationXRef.current = 0
      }
    }
    if (groupRef.current) {
      groupRef.current.rotation.y = rotationYRef.current
      groupRef.current.rotation.x = rotationXRef.current
    }
  })

  // Create bounding box helper
  const boxHelper = useMemo(() => {
    if (!SHOW_BOUNDING_BOX) return null
    const helper = new Box3Helper(debugBox, new Color(0x00ff00))
    return helper
  }, [debugBox])

  return (
    <group ref={groupRef}>
      <group position={groupOffset}>
        <primitive object={clonedScene} scale={scale} />
      </group>
      {/* Debug: Bounding Box */}
      {SHOW_BOUNDING_BOX && boxHelper && (
        <primitive object={boxHelper} />
      )}
      {/* Debug: Center cross (origin marker) */}
      {SHOW_BOUNDING_BOX && (
        <>
          <mesh position={[0, 0, 0]}>
            <sphereGeometry args={[0.05, 16, 16]} />
            <meshBasicMaterial color="red" />
          </mesh>
          {/* X axis - red */}
          <mesh position={[0.5, 0, 0]}>
            <boxGeometry args={[1, 0.02, 0.02]} />
            <meshBasicMaterial color="red" />
          </mesh>
          {/* Y axis - green */}
          <mesh position={[0, 0.5, 0]}>
            <boxGeometry args={[0.02, 1, 0.02]} />
            <meshBasicMaterial color="green" />
          </mesh>
          {/* Z axis - blue */}
          <mesh position={[0, 0, 0.5]}>
            <boxGeometry args={[0.02, 0.02, 1]} />
            <meshBasicMaterial color="blue" />
          </mesh>
        </>
      )}
    </group>
  )
}

function PLYModel({ url }: { url: string }) {
  const containerRef = useRef<HTMLDivElement>(null)
  const viewerRef = useRef<GaussianSplats3D.Viewer | null>(null)

  useEffect(() => {
    if (!containerRef.current || viewerRef.current) return

    // Create standalone Gaussian Splat viewer
    const viewer = new GaussianSplats3D.Viewer({
      cameraUp: [0, 1, 0],
      initialCameraPosition: [0, 0, 3],
      initialCameraLookAt: [0, 0, 0],
      selfDrivenMode: true,
      useBuiltInControls: true,
      rootElement: containerRef.current,
      sharedMemoryForWorkers: false,
    })

    viewerRef.current = viewer

    viewer.addSplatScene(url, {
      // SceneFormat.Ply — types are stale, the option is honoured at runtime
      format: (GaussianSplats3D as any).SceneFormat?.Ply ?? 0,
      splatAlphaRemovalThreshold: 5,
      showLoadingUI: true,
      position: [0, -0.5, 0],
      rotation: [0, 0, 0, 1],
      scale: [1.5, 1.5, 1.5],
    } as any).then(() => {
      console.log('Gaussian Splat loaded:', url)
      viewer.start()
    }).catch((err: Error) => {
      console.error('Error loading Gaussian Splat:', err)
    })

    return () => {
      if (viewerRef.current) {
        const r = (viewerRef.current as any).dispose()
        if (r && typeof r.catch === 'function') {
          r.catch((err: unknown) => console.warn('[Splat] dispose:', err))
        }
        viewerRef.current = null
      }
    }
  }, [url])

  // PLY uses its own canvas, return a portal container
  return null
}

// Camera position calculator - calculates optimal positions based on bounding box
// FOV = 50 degrees (same as CameraController PerspectiveCamera)
const CAMERA_FOV = 50
const FOV_RAD = (CAMERA_FOV * Math.PI) / 180

function calculateCameraPosition(viewMode: ViewMode, transformedBBox: { min: { x: number; y: number; z: number }; max: { x: number; y: number; z: number }; center: { x: number; y: number; z: number } } | null) {

  // Hero mode: Fit entire statue in view with margin
  if (viewMode === 'hero') {
    if (transformedBBox) {
      const bboxHeight = transformedBBox.max.y - transformedBBox.min.y
      const bboxWidth = transformedBBox.max.x - transformedBBox.min.x

      // Calculate distance to fit height in view: d = (h/2) / tan(fov/2)
      // Add 20% margin
      const margin = 1.2
      const distanceForHeight = (bboxHeight * margin / 2) / Math.tan(FOV_RAD / 2)

      // Also consider width (assume ~16:9 aspect, hfov ≈ fov * 1.5)
      const distanceForWidth = (bboxWidth * margin / 2) / Math.tan(FOV_RAD * 0.75 / 2)

      // Use the larger distance to ensure full visibility
      const distance = Math.max(distanceForHeight, distanceForWidth, 2.0) // min 2.0

      return {
        position: [0, transformedBBox.center.y, distance] as [number, number, number],
        lookAt: [0, transformedBBox.center.y, 0] as [number, number, number],
      }
    }
    // Fallback
    return {
      position: [0, 0, 4] as [number, number, number],
      lookAt: [0, 0, 0] as [number, number, number],
    }
  }

  // Detail mode (first click): Look at upper third (head area), closer zoom
  if (viewMode === 'detail') {
    if (transformedBBox) {
      const bboxHeight = transformedBBox.max.y - transformedBBox.min.y
      const bboxWidth = transformedBBox.max.x - transformedBBox.min.x

      // Tall-and-thin sculptures (e.g. "Geerdeter" — a 2m vertical
      // column on a small base, aspect ~7.6:1) don't have a head to
      // frame — the entire silhouette IS the subject. If we zoom to
      // the upper third the way we do for figurative pieces, the
      // visitor sees a thin strip of nothing. Anna's WhatsApp on
      // 2026-07-02: "hab gerade gesehen, dass beim geerdeten bei
      // Detail nur der unterste Teil sichtbar wird. Die Skulptur ist
      // ja knapp 2 Meter." Threshold 3.0 catches column-like works
      // while leaving normal figures (aspect 1.5–2.5) on the head-
      // framed path.
      const aspectRatio = bboxHeight / Math.max(bboxWidth, 0.001)
      if (aspectRatio > 3.0) {
        // Fit the full sculpture with a moderate margin — same idea as
        // hero mode but closer (margin 1.05 vs 1.2) so it still reads
        // as "detail" rather than "hero".
        const margin = 1.05
        const distanceForHeight = (bboxHeight * margin / 2) / Math.tan(FOV_RAD / 2)
        const distanceForWidth = (bboxWidth * margin / 2) / Math.tan(FOV_RAD * 0.75 / 2)
        const distance = Math.max(distanceForHeight, distanceForWidth, 2.0)
        // Slight side-offset so it still feels different from hero.
        const xOffset = distance * 0.25
        return {
          position: [xOffset, transformedBBox.center.y, distance] as [number, number, number],
          lookAt: [0, transformedBBox.center.y, 0] as [number, number, number],
        }
      }

      const lookAtY = transformedBBox.center.y + bboxHeight * 0.35  // Upper third

      // Zoom to show ~40% of height
      const visibleHeight = bboxHeight * 0.4
      const distance = (visibleHeight / 2) / Math.tan(FOV_RAD / 2)

      // Offset camera to side for dramatic angle
      const xOffset = distance * 0.6
      const zDistance = distance * 0.8

      return {
        position: [xOffset, lookAtY + 0.1, zDistance] as [number, number, number],
        lookAt: [0, lookAtY, 0] as [number, number, number],
      }
    }
    // Fallback
    return {
      position: [0.8, 1.2, 1.0] as [number, number, number],
      lookAt: [0, 1.0, 0] as [number, number, number],
    }
  }

  // Closeup mode (second click): Look at center (torso area), even closer
  if (viewMode === 'closeup') {
    if (transformedBBox) {
      const bboxHeight = transformedBBox.max.y - transformedBBox.min.y
      const lookAtY = transformedBBox.center.y  // Center/torso

      // Zoom to show ~50% of height
      const visibleHeight = bboxHeight * 0.5
      const distance = (visibleHeight / 2) / Math.tan(FOV_RAD / 2)

      // More side offset
      const xOffset = distance * 0.9
      const zDistance = distance * 0.9

      return {
        position: [xOffset, lookAtY + 0.15, zDistance] as [number, number, number],
        lookAt: [0, lookAtY, 0] as [number, number, number],
      }
    }
    // Fallback
    return {
      position: [1.5, 0.3, 1.5] as [number, number, number],
      lookAt: [0, 0, 0] as [number, number, number],
    }
  }

  // Default fallback
  return {
    position: [0, 0, 4] as [number, number, number],
    lookAt: [0, 0, 0] as [number, number, number],
  }
}

import { ViewMode } from '@/types/three'
import { PlyBBox } from '@/types/artwork'

interface PLYViewerProps {
  url: string
  viewMode: ViewMode
  onClick?: () => void
  plyBBox?: PlyBBox
}

// Separate component for PLY that renders outside R3F
export function PLYViewer({ url, viewMode, onClick, plyBBox }: PLYViewerProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const viewerRef = useRef<GaussianSplats3D.Viewer | null>(null)
  const splatMeshRef = useRef<any>(null)  // Reference to splat mesh for rotation
  const rotationY = useRef(0)
  const isDragging = useRef(false)
  const lastX = useRef(0)
  const dragDistance = useRef(0)
  const [isLoading, setIsLoading] = useState(true)
  const { accentColor } = useTheme()

  // Calculate transformed bbox once (same transform as splat loading)
  const transformedBBox = useMemo(() => {
    if (!plyBBox) return null
    const scale = 2.8
    const yOffset = -0.6
    // PLY file uses Y-axis DOWN (Polycam convention) - flip Y
    const flippedMinY = -plyBBox.max.y
    const flippedMaxY = -plyBBox.min.y
    return {
      min: {
        x: plyBBox.min.x * scale,
        y: flippedMinY * scale + yOffset,
        z: plyBBox.min.z * scale,
      },
      max: {
        x: plyBBox.max.x * scale,
        y: flippedMaxY * scale + yOffset,
        z: plyBBox.max.z * scale,
      },
      center: {
        x: ((plyBBox.min.x + plyBBox.max.x) / 2) * scale,
        y: ((flippedMinY + flippedMaxY) / 2) * scale + yOffset,
        z: ((plyBBox.min.z + plyBBox.max.z) / 2) * scale,
      },
    }
  }, [plyBBox])
  const transformedBBoxRef = useRef(transformedBBox)
  transformedBBoxRef.current = transformedBBox

  // Initialize viewer - load PLY directly by URL (library needs file extension)
  useEffect(() => {
    if (!containerRef.current || viewerRef.current) return

    // Use bbox-based camera position (same as GLB CameraController)
    const initialCam = calculateCameraPosition(viewMode, transformedBBoxRef.current)

    const loadScene = async () => {
      try {
        // Create viewer
        const viewer = new GaussianSplats3D.Viewer({
          cameraUp: [0, 1, 0],
          initialCameraPosition: [...initialCam.position] as [number, number, number],
          initialCameraLookAt: [...initialCam.lookAt] as [number, number, number],
          selfDrivenMode: true,
          useBuiltInControls: false,
          rootElement: containerRef.current!,
          sharedMemoryForWorkers: false,
        })

        viewerRef.current = viewer

        // Force the splat renderer to clear with alpha 0 so the body's
        // radial gradient shows through behind the sculpture instead of
        // being hidden by an opaque background.
        try {
          const v = viewer as any
          const renderer = v.renderer ?? v.threeRenderer
          if (renderer && typeof renderer.setClearColor === 'function') {
            renderer.setClearColor(0x000000, 0)
            if (typeof renderer.setClearAlpha === 'function') renderer.setClearAlpha(0)
          }
          if (v.threeScene) v.threeScene.background = null
        } catch (err) {
          console.warn('[PLY] could not set transparent clear:', err)
        }

        // Glow-Ring removed per design — Anna wants the splat to read
        // as a sculpture in a museum, not framed inside a neon torus.
        // glowRingRef stays defined so the cleanup branch below stays
        // a no-op without further conditionals.
        const threeScene = (viewer as any).threeScene
        if (threeScene) {

          // Debug: Add bounding box and axes
          if (SHOW_BOUNDING_BOX) {
            // Transform bbox with same scale/offset as splat loading
            const scale = 2.8
            const yOffset = -0.6

            console.log('=== PLY DEBUG ===')
            console.log('plyBBox prop:', plyBBox)

            let plyBox: Box3
            if (plyBBox) {
              // PLY file uses Y-axis DOWN (Polycam convention)
              // The viewer flips Y via rotation, so we need to flip Y in bbox too
              // Original: min.y is top (negative), max.y is bottom (less negative)
              // After flip: -max.y becomes min.y, -min.y becomes max.y
              const flippedMinY = -plyBBox.max.y
              const flippedMaxY = -plyBBox.min.y

              plyBox = new Box3(
                new Vector3(
                  plyBBox.min.x * scale,
                  flippedMinY * scale + yOffset,
                  plyBBox.min.z * scale
                ),
                new Vector3(
                  plyBBox.max.x * scale,
                  flippedMaxY * scale + yOffset,
                  plyBBox.max.z * scale
                )
              )
              console.log('PLY bbox (Y-flipped + transformed):', {
                min: plyBox.min.toArray().map(v => v.toFixed(2)),
                max: plyBox.max.toArray().map(v => v.toFixed(2))
              })
            } else {
              console.log('NO plyBBox - using fallback')
              // Fallback estimate
              plyBox = new Box3(
                new Vector3(-scale * 0.5, -scale * 0.5 + yOffset, -scale * 0.5),
                new Vector3(scale * 0.5, scale * 0.5 + yOffset, scale * 0.5)
              )
            }
            const plyBoxHelper = new Box3Helper(plyBox, new Color(0x00ff00))
            threeScene.add(plyBoxHelper)
            ;(viewer as any)._debugBoxHelper = plyBoxHelper

            // Origin sphere
            const originGeo = new SphereGeometry(0.05, 16, 16)
            const originMat = new MeshBasicMaterial({ color: 0xff0000 })
            const originSphere = new Mesh(originGeo, originMat)
            threeScene.add(originSphere)

            // X axis (red)
            const xGeo = new BoxGeometry(1, 0.02, 0.02)
            const xMat = new MeshBasicMaterial({ color: 0xff0000 })
            const xAxis = new Mesh(xGeo, xMat)
            xAxis.position.set(0.5, 0, 0)
            threeScene.add(xAxis)

            // Y axis (green)
            const yGeo = new BoxGeometry(0.02, 1, 0.02)
            const yMat = new MeshBasicMaterial({ color: 0x00ff00 })
            const yAxis = new Mesh(yGeo, yMat)
            yAxis.position.set(0, 0.5, 0)
            threeScene.add(yAxis)

            // Z axis (blue)
            const zGeo = new BoxGeometry(0.02, 0.02, 1)
            const zMat = new MeshBasicMaterial({ color: 0x0000ff })
            const zAxis = new Mesh(zGeo, zMat)
            zAxis.position.set(0, 0, 0.5)
            threeScene.add(zAxis)
          }
        }

        // The api-storage URL has no .ply suffix, so the library can't infer
        // the format from the path — pass it explicitly.
        await viewer.addSplatScene(url, {
          format: (GaussianSplats3D as any).SceneFormat?.Ply ?? 0,
          splatAlphaRemovalThreshold: 5,
          showLoadingUI: false,
          position: [0, -0.6, 0],
          rotation: [1, 0, 0, 0],
          scale: [2.8, 2.8, 2.8],
        } as any)

        // Store reference to splat mesh for rotation (like GLB groupRef)
        const splatMesh = (viewer as any).splatMesh
        if (splatMesh) {
          splatMeshRef.current = splatMesh
          console.log('SplatMesh found for rotation')
        }

        console.log('Gaussian Splat loaded:', url)
        setIsLoading(false)
        viewer.start()

      } catch (err) {
        console.error('Error loading Gaussian Splat:', err)
        setIsLoading(false)
      }
    }

    loadScene()

    return () => {
      // Glow ring removed — nothing to dispose on the cleanup branch
      // beyond the splat viewer itself.
      if (viewerRef.current) {
        try {
          const viewer = viewerRef.current as any
          // Stop rendering first if method exists
          if (typeof viewer.stop === 'function') {
            viewer.stop()
          }
          // dispose() returns a promise that internally calls
          // document.body.removeChild(rootElement); when the host React
          // tree has already removed that element the call throws a
          // NotFoundError inside the promise. Swallow it — the viewer
          // has finished tearing down its own state by that point.
          const result = viewer.dispose()
          if (result && typeof result.catch === 'function') {
            result.catch((err: unknown) => {
              console.warn('[PLY] Viewer dispose promise:', err)
            })
          }
        } catch (e) {
          console.warn('[PLY] Viewer dispose error:', e)
        }
        viewerRef.current = null
      }
    }
  }, [url, accentColor])

  // Track current camera state for smooth transitions
  // Use orbit (radius + angle) because Gaussian Splats require camera movement, not mesh rotation
  const heroCam = calculateCameraPosition('hero', transformedBBoxRef.current)
  const currentCamState = useRef<{ radius: number; y: number; angle: number; lookAtY: number }>({
    radius: Math.sqrt(heroCam.position[0] ** 2 + heroCam.position[2] ** 2),
    y: heroCam.position[1],
    angle: Math.atan2(heroCam.position[0], heroCam.position[2]),
    lookAtY: heroCam.lookAt[1],
  })

  // Animate camera when viewMode changes (orbit-based for Gaussian Splats)
  useEffect(() => {
    if (!viewerRef.current) return

    // Use bbox-based camera position
    const targetCam = calculateCameraPosition(viewMode, transformedBBoxRef.current)
    const viewer = viewerRef.current
    const camera = (viewer as any).camera

    if (!camera) return

    // Calculate target orbit values
    const targetRadius = Math.sqrt(targetCam.position[0] ** 2 + targetCam.position[2] ** 2)
    const targetBaseAngle = Math.atan2(targetCam.position[0], targetCam.position[2])
    // Add current rotation to target angle to preserve user's rotation
    const targetAngle = targetBaseAngle + rotationY.current

    // Start from current state
    const startState = { ...currentCamState.current }
    const endState = {
      radius: targetRadius,
      y: targetCam.position[1],
      angle: targetAngle,
      lookAtY: targetCam.lookAt[1],
    }

    const duration = 1000
    const startTime = performance.now()

    const animate = () => {
      const elapsed = performance.now() - startTime
      const progress = Math.min(elapsed / duration, 1)
      const eased = progress < 0.5
        ? 2 * progress * progress
        : 1 - Math.pow(-2 * progress + 2, 2) / 2

      const currentRadius = startState.radius + (endState.radius - startState.radius) * eased
      const currentY = startState.y + (endState.y - startState.y) * eased
      const currentAngle = startState.angle + (endState.angle - startState.angle) * eased
      const currentLookAtY = startState.lookAtY + (endState.lookAtY - startState.lookAtY) * eased

      camera.position.set(
        Math.sin(currentAngle) * currentRadius,
        currentY,
        Math.cos(currentAngle) * currentRadius
      )
      camera.lookAt(0, currentLookAtY, 0)

      // Update current state
      currentCamState.current = {
        radius: currentRadius,
        y: currentY,
        angle: currentAngle,
        lookAtY: currentLookAtY,
      }

      if (progress < 1) {
        requestAnimationFrame(animate)
      }
    }

    animate()
  }, [viewMode])

  // Y-axis rotation via drag - orbit camera (required for Gaussian Splats)
  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const handleStart = (clientX: number) => {
      isDragging.current = true
      lastX.current = clientX
      dragDistance.current = 0
    }

    const handleMove = (clientX: number) => {
      if (!isDragging.current || !viewerRef.current) return

      const deltaX = clientX - lastX.current
      dragDistance.current += Math.abs(deltaX)
      rotationY.current -= deltaX * 0.01  // Inverted for natural feel
      lastX.current = clientX

      // Orbit camera around Y axis
      const camera = (viewerRef.current as any).camera
      if (!camera) return

      currentCamState.current.angle -= deltaX * 0.01
      camera.position.x = Math.sin(currentCamState.current.angle) * currentCamState.current.radius
      camera.position.z = Math.cos(currentCamState.current.angle) * currentCamState.current.radius
      camera.lookAt(0, currentCamState.current.lookAtY, 0)
    }

    const handleEnd = () => {
      isDragging.current = false
    }

    const onMouseDown = (e: MouseEvent) => handleStart(e.clientX)
    const onMouseMove = (e: MouseEvent) => handleMove(e.clientX)
    const onMouseUp = () => handleEnd()
    const onTouchStart = (e: TouchEvent) => handleStart(e.touches[0].clientX)
    const onTouchMove = (e: TouchEvent) => handleMove(e.touches[0].clientX)
    const onTouchEnd = () => handleEnd()

    container.addEventListener('mousedown', onMouseDown)
    container.addEventListener('mousemove', onMouseMove)
    container.addEventListener('mouseup', onMouseUp)
    container.addEventListener('mouseleave', onMouseUp)
    container.addEventListener('touchstart', onTouchStart)
    container.addEventListener('touchmove', onTouchMove)
    container.addEventListener('touchend', onTouchEnd)

    return () => {
      container.removeEventListener('mousedown', onMouseDown)
      container.removeEventListener('mousemove', onMouseMove)
      container.removeEventListener('mouseup', onMouseUp)
      container.removeEventListener('mouseleave', onMouseUp)
      container.removeEventListener('touchstart', onTouchStart)
      container.removeEventListener('touchmove', onTouchMove)
      container.removeEventListener('touchend', onTouchEnd)
    }
  }, [])  // No dependencies - rotation is independent of viewMode

  // Handle click - only trigger if not dragging
  const handleClick = () => {
    if (dragDistance.current < 10 && onClick) {
      onClick()
    }
  }

  return (
    <div
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        background: 'transparent',
      }}
    >
      {/* PLY Canvas */}
      <div
        ref={containerRef}
        onClick={handleClick}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          cursor: 'grab',
        }}
      />

      {/* Custom Loading Overlay */}
      {isLoading && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'transparent',
            zIndex: 10,
          }}
        >
          {/* Animated Ring */}
          <div
            style={{
              width: 60,
              height: 60,
              border: `2px solid ${accentColor}20`,
              borderTop: `2px solid ${accentColor}`,
              borderRadius: '50%',
              animation: 'plyLoaderSpin 1s linear infinite',
            }}
          />
          <style>{`
            @keyframes plyLoaderSpin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      )}
    </div>
  )
}

/**
 * Ground-level loading spinner.
 *
 * Shown by <Suspense> while a GLB is still downloading. The six featured
 * sculptures add up to ~8.7 MB, and the meshopt decode on top means a
 * visitor on a normal connection stares at an empty stage for a few
 * seconds — so the stage gets a light of its own instead.
 *
 * Design: a thin arc lying flat on the floor where the sculpture's base
 * will stand, sweeping around like a lighthouse beam, over a faint full
 * ring that marks the spot. Deliberately calm — this is a museum, not a
 * dashboard.
 *
 * Placement: the model is scaled to targetHeight 2.85, centred on the
 * origin and nudged down by 0.15, so its base sits at
 *   -(2.85 / 2) - 0.15 ≈ -1.58
 * The ring sits marginally below that so it never clips into the plinth.
 */
function LoadingPlaceholder() {
  const arcRef = useRef<Mesh>(null)
  const GOLD = '#B7924E'      // --glow-gold, matches the site palette
  const FLOOR_Y = -1.6

  useFrame((_, delta) => {
    if (arcRef.current) {
      // ~1.2 rad/s — one turn every ~5 s. Slow enough to read as
      // "working", not as "hurry up".
      arcRef.current.rotation.z -= delta * 1.2
    }
  })

  return (
    <group position={[0, FLOOR_Y, 0]} rotation={[-Math.PI / 2, 0, 0]}>
      {/* Faint full ring — marks where the sculpture will appear */}
      <mesh>
        <ringGeometry args={[0.62, 0.66, 64]} />
        <meshBasicMaterial color={GOLD} transparent opacity={0.16} toneMapped={false} />
      </mesh>

      {/* Sweeping arc — the actual motion cue */}
      <mesh ref={arcRef}>
        <ringGeometry args={[0.6, 0.68, 48, 1, 0, Math.PI * 0.45]} />
        <meshBasicMaterial color={GOLD} transparent opacity={0.85} toneMapped={false} />
      </mesh>
    </group>
  )
}

export function SculptureModel({
  artwork,
  onBBoxCalculated,
}: SculptureModelProps) {
  const hasGLB = !!artwork.files.glb
  const hasPLY = !!artwork.files.ply

  // Prefer GLB over PLY
  const modelUrl = hasGLB ? artwork.files.glb : hasPLY ? artwork.files.ply : null

  if (!modelUrl) {
    return <LoadingPlaceholder />
  }

  const encodedUrl = mediaUrl(modelUrl) || ''
  // The picked field tells us the format — the URL itself may be opaque
  // (e.g. api-storage /storage/media/{id}) without a recognizable extension.
  const isGLB = hasGLB

  return (
    <Suspense fallback={<LoadingPlaceholder />}>
      <group position={[0, 0, 0]}>
        {isGLB ? (
          <GLBModel url={encodedUrl} onBBoxCalculated={onBBoxCalculated} />
        ) : (
          <PLYModel url={encodedUrl} />
        )}
      </group>
    </Suspense>
  )
}
